'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import Link from '@/components/ui/Link';
import { useEffect, useRef, useState, useId } from 'react';
import { useDevice } from '@/contexts/DeviceContext';
import { PageComponent, ComponentVisibility } from '@/lib/types/pageBuilder';
import { getSectionComponent } from '@/lib/sectionRegistry';
import { IncludeSectionApiData } from '@/lib/pageUtils';
import AdvertisePopup from '@/components/ads/AdvertisePopup';
import { getWidgetLinkProps, BLUR_PLACEHOLDER } from '@/lib/componentutills';
import { isLighthouseClient } from '@/lib/isLighthouse';

// ============================================================
// HTML Widget Component - Renders custom HTML, CSS, and JavaScript
// ============================================================
interface HTMLWidgetProps {
  id: string;
  htmlContent: string;
  cssContent?: string;
  jsContent?: string;
  visibilityClasses: string;
}

// Sanitize string to remove invalid characters that cause DOMException in some browsers
function sanitizeForDOM(str: string): string {
  if (!str) return '';
  try {
    // Remove null bytes, control characters, and other problematic Unicode characters
    // - 0x00-0x08: C0 control characters (except tab, newline, carriage return)
    // - 0x0B, 0x0C: Vertical tab, form feed
    // - 0x0E-0x1F: More C0 control characters
    // - 0x7F: DEL character
    // - 0x80-0x9F: C1 control characters
    // - 0xFFFE, 0xFFFF: Non-characters
    // eslint-disable-next-line no-control-regex
    return str.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '').replace(/[\x80-\x9F]/g, '').replace(/\uFFFE|\uFFFF/g, '');
  } catch {
    // If sanitization fails, return empty string to prevent DOM errors
    return '';
  }
}
function HTMLWidget(t0) {
  const $ = _c(28);
  const {
    id,
    htmlContent,
    cssContent,
    jsContent,
    visibilityClasses
  } = t0;
  const iframeRef = useRef(null);
  const writtenRef = useRef(false);
  const [iframeHeight, setIframeHeight] = useState(0);
  const t1 = useId();
  let t2;
  if ($[0] !== t1) {
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = /^(\d)/;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    t2 = t1.replace(/:/g, "").replace(t3, "w$1");
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const uniqueId = t2;
  let t3;
  if ($[3] !== id) {
    t3 = id.substring(0, 8);
    $[3] = id;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const widgetId = `html-widget-${t3}-${uniqueId}`;
  let t4;
  let t5;
  if ($[5] !== widgetId) {
    t4 = () => {
      const handler = event => {
        if (event.data && event.data.type === "html-widget-resize" && event.data.widgetId === widgetId) {
          const newHeight = Math.ceil(event.data.height);
          setIframeHeight(prev => Math.abs(newHeight - prev) > 5 ? newHeight : prev);
        }
      };
      window.addEventListener("message", handler);
      return () => window.removeEventListener("message", handler);
    };
    t5 = [widgetId];
    $[5] = widgetId;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[8] !== cssContent || $[9] !== htmlContent || $[10] !== jsContent || $[11] !== widgetId) {
    t6 = () => {
      const iframe = iframeRef.current;
      if (!iframe || writtenRef.current) {
        return;
      }
      const sanitizedHtml = sanitizeForDOM(htmlContent);
      const currentOrigin = typeof window !== "undefined" ? window.location.origin : "";
      const rewrittenHtml = currentOrigin ? sanitizedHtml.replace(/href\s*=\s*"(https?:\/\/(?:www\.)?dinamani\.com)([^"]*)"/gi, (_, _domain, path) => `href="${currentOrigin}${path}"`) : sanitizedHtml;
      const cssBlock = cssContent ? "<style>" + sanitizeForDOM(cssContent) + "</style>" : "";
      const jsBlock = jsContent ? "<script>" + sanitizeForDOM(jsContent) + "</script>" : "";
      const resizeScript = `<script>
(function(){
  var _wid = '${widgetId}';
  var _lastH = 0;
  var _timer = null;
  function _sh(){
    if (_timer) return;
    _timer = setTimeout(function(){
      _timer = null;
      try {
        var h = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
        if (Math.abs(h - _lastH) > 5) {
          _lastH = h;
          window.parent.postMessage({type:'html-widget-resize',widgetId:_wid,height:h},'*');
        }
      } catch(e){}
    }, 150);
  }
  window.addEventListener('load', _sh);
  window.addEventListener('resize', _sh);
  if(typeof MutationObserver !== 'undefined'){
    new MutationObserver(_sh).observe(document.body,{childList:true,subtree:true,attributes:true});
  }
  setInterval(_sh, 3000);
})();
<\/script>`;
      const isFullDocument = /<!DOCTYPE|<html[\s>]/i.test(rewrittenHtml);
      let fullDoc;
      if (isFullDocument) {
        let doc = rewrittenHtml;
        if (/<head[^>]*>/i.test(doc)) {
          doc = doc.replace(/<head[^>]*>/i, "$&<base target=\"_parent\"><meta name=\"viewport\" content=\"width=device-width, initial-scale=1.0\">");
        } else {
          if (/<html[^>]*>/i.test(doc)) {
            doc = doc.replace(/<html[^>]*>/i, "$&<head><base target=\"_parent\"><meta name=\"viewport\" content=\"width=device-width, initial-scale=1.0\"></head>");
          }
        }
        if (cssBlock) {
          if (/<\/head>/i.test(doc)) {
            doc = doc.replace(/<\/head>/i, cssBlock + "</head>");
          }
        }
        const scripts = (jsBlock || "") + resizeScript;
        if (/<\/body>/i.test(doc)) {
          doc = doc.replace(/<\/body>/i, scripts + "</body>");
        } else {
          doc = doc + scripts;
        }
        fullDoc = doc;
      } else {
        fullDoc = "<base target=\"_parent\"><meta name=\"viewport\" content=\"width=device-width, initial-scale=1.0\">" + cssBlock + rewrittenHtml + jsBlock + resizeScript;
      }
      const tryWrite = () => {
        if (writtenRef.current) {
          return true;
        }
        try {
          iframe.srcdoc = fullDoc;
          writtenRef.current = true;
          return true;
        } catch {
          return false;
        }
      };
      if (!tryWrite()) {
        const onLoad = () => tryWrite();
        iframe.addEventListener("load", onLoad, {
          once: true
        });
        const timer = setTimeout(() => tryWrite(), 200);
        return () => {
          iframe.removeEventListener("load", onLoad);
          clearTimeout(timer);
        };
      }
    };
    t7 = [htmlContent, cssContent, jsContent, widgetId];
    $[8] = cssContent;
    $[9] = htmlContent;
    $[10] = jsContent;
    $[11] = widgetId;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  const t8 = `html-widget-wrapper ${visibilityClasses}`;
  let t9;
  if ($[14] !== t8) {
    t9 = t8.trim();
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      width: "100%",
      maxWidth: "100vw",
      overflow: "hidden"
    };
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = `${iframeHeight}px`;
  let t12;
  if ($[17] !== t11) {
    t12 = {
      width: "100%",
      height: t11,
      border: "none",
      display: "block",
      maxWidth: "100%"
    };
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== id) {
    t13 = id.substring(0, 8);
    $[19] = id;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const t14 = `HTML Widget ${t13}`;
  let t15;
  if ($[21] !== t12 || $[22] !== t14 || $[23] !== widgetId) {
    t15 = <iframe ref={iframeRef} id={widgetId} style={t12} scrolling="no" title={t14} />;
    $[21] = t12;
    $[22] = t14;
    $[23] = widgetId;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t15 || $[26] !== t9) {
    t16 = <div className={t9} style={t10}>{t15}</div>;
    $[25] = t15;
    $[26] = t9;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  return t16;
}

// ============================================================
// Widget Renderer Props
// ============================================================
interface WidgetRendererProps {
  component: PageComponent;
  includeSectionApiData?: IncludeSectionApiData;
}
function useIsMobile(t0) {
  const $ = _c(6);
  const breakpoint = t0 === undefined ? 768 : t0;
  const {
    isMobile,
    isTablet,
    deviceDetected
  } = useDevice();
  if (breakpoint === 1024) {
    const t1 = isMobile || isTablet;
    let t2;
    if ($[0] !== deviceDetected || $[1] !== t1) {
      t2 = [t1, deviceDetected];
      $[0] = deviceDetected;
      $[1] = t1;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t1;
  if ($[3] !== deviceDetected || $[4] !== isMobile) {
    t1 = [isMobile, deviceDetected];
    $[3] = deviceDetected;
    $[4] = isMobile;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}

// Helper function to get the appropriate ad code based on device and config
function getAdCode(component: PageComponent, isMobile: boolean): string | undefined {
  // Check new format: desktopAdCode / mobileAdCode
  if (isMobile && component.mobileAdCode) {
    return component.mobileAdCode;
  }
  if (!isMobile && component.desktopAdCode) {
    return component.desktopAdCode;
  }

  // If useInstanceAdCode is true, use device-specific codes (legacy format)
  if (component.useInstanceAdCode) {
    if (isMobile && component.instanceMobileAdCode) {
      return component.instanceMobileAdCode;
    }
    if (!isMobile && component.instanceDesktopAdCode) {
      return component.instanceDesktopAdCode;
    }
  }
  // Fallback to default ad_code
  return component.ad_code;
}

/**
 * Extract ad slot size from GPT ad code (defineSlot call).
 * Returns { width, height } or null if not found.
 */
function extractAdSize(adCode: string): {
  width: number;
  height: number;
} | null {
  if (!adCode) return null;
  const match = adCode.match(/defineSlot\(\s*['"][^'"]+['"]\s*,\s*(\[[^\]]+\])\s*,/);
  if (!match) return null;
  try {
    const sizes = JSON.parse(match[1]);
    // sizes can be [300, 250] or [[300, 250], [728, 90]]
    if (Array.isArray(sizes[0])) {
      return {
        width: sizes[0][0],
        height: sizes[0][1]
      };
    }
    if (typeof sizes[0] === 'number' && typeof sizes[1] === 'number') {
      return {
        width: sizes[0],
        height: sizes[1]
      };
    }
  } catch {/* ignore */}
  return null;
}

// ============================================================
// Custom Ad Widget Component - Renders custom HTML ads with CSS and JS
// ============================================================
interface CustomAdWidgetProps {
  id: string;
  slotId: string;
  htmlContent: string;
  cssContent?: string;
  jsContent?: string;
  width?: string;
  height?: string;
  visibilityClasses: string;
}
function CustomAdWidget(t0) {
  const $ = _c(34);
  const {
    slotId,
    htmlContent,
    cssContent,
    jsContent,
    width,
    height,
    visibilityClasses
  } = t0;
  const containerRef = useRef(null);
  const t1 = useId();
  let t2;
  if ($[0] !== t1) {
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = /^(\d)/;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    t2 = t1.replace(/:/g, "").replace(t3, "w$1");
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const uniqueId = t2;
  let t3;
  if ($[3] !== slotId) {
    t3 = slotId.substring(0, 8);
    $[3] = slotId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const widgetId = `custom-ad-${t3}-${uniqueId}`;
  let t4;
  let t5;
  if ($[5] !== htmlContent || $[6] !== jsContent || $[7] !== widgetId) {
    t4 = () => {
      if (!containerRef.current) {
        return;
      }
      if (isLighthouseClient()) {
        return;
      }
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = sanitizeForDOM(htmlContent);
      const scripts = tempDiv.querySelectorAll("script");
      const scriptsToExecute = [];
      scripts.forEach(script => {
        if (script.src) {
          scriptsToExecute.push({
            src: script.src
          });
        } else {
          if (script.textContent) {
            scriptsToExecute.push({
              content: sanitizeForDOM(script.textContent)
            });
          }
        }
        script.remove();
      });
      containerRef.current.innerHTML = tempDiv.innerHTML;
      scriptsToExecute.forEach(_temp);
      if (jsContent) {
        ;
        try {
          const sanitizedJs = sanitizeForDOM(jsContent);
          const fn_0 = new Function("widgetId", `
          const adContainer = document.getElementById(widgetId);
          if (!adContainer) return;
          try {
            ${sanitizedJs}
          } catch (e) {
            // Script execution error
          }
        `);
          fn_0(widgetId);
        } catch (t6) {
          const error = t6;
        }
      }
    };
    t5 = [htmlContent, jsContent, widgetId];
    $[5] = htmlContent;
    $[6] = jsContent;
    $[7] = widgetId;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  let scopedCss = "";
  if (cssContent) {
    ;
    try {
      let t7;
      if ($[10] !== cssContent || $[11] !== widgetId) {
        const sanitizedCss = sanitizeForDOM(cssContent);
        let t8;
        if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = /([^{}]+)(\{[^}]*\})/g;
          $[13] = t8;
        } else {
          t8 = $[13];
        }
        let t9;
        if ($[14] !== widgetId) {
          t9 = (match, selector, rules) => {
            if (selector.includes(widgetId) || selector.trim().startsWith("@")) {
              return match;
            }
            const scopedSelectors = selector.split(",").map(s => {
              const trimmed = s.trim();
              if (trimmed.startsWith("@") || trimmed.includes(widgetId)) {
                return trimmed;
              }
              return `#${widgetId} ${trimmed}`;
            }).join(", ");
            return `${scopedSelectors}${rules}`;
          };
          $[14] = widgetId;
          $[15] = t9;
        } else {
          t9 = $[15];
        }
        t7 = sanitizedCss.replace(t8, t9);
        $[10] = cssContent;
        $[11] = widgetId;
        $[12] = t7;
      } else {
        t7 = $[12];
      }
      scopedCss = t7;
    } catch (t6) {
      scopedCss = cssContent;
    }
  }
  const t6 = width || "100%";
  const t7 = height || "auto";
  let t8;
  if ($[16] !== t6 || $[17] !== t7) {
    t8 = {
      width: t6,
      height: t7,
      overflow: "hidden",
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
    };
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const containerStyle = t8;
  const t9 = width ? "auto" : "100%";
  let t10;
  if ($[19] !== t9) {
    t10 = {
      width: t9,
      maxWidth: "100%"
    };
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const wrapperStyle = t10;
  const t11 = `custom-ad-wrapper flex justify-center items-center w-full ${visibilityClasses}`;
  let t12;
  if ($[21] !== t11) {
    t12 = t11.trim();
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== scopedCss) {
    t13 = scopedCss && <style dangerouslySetInnerHTML={{
      __html: scopedCss
    }} />;
    $[23] = scopedCss;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== containerStyle || $[26] !== widgetId) {
    t14 = <div id={widgetId} ref={containerRef} className="custom-ad-content" style={containerStyle} />;
    $[25] = containerStyle;
    $[26] = widgetId;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== slotId || $[29] !== t12 || $[30] !== t13 || $[31] !== t14 || $[32] !== wrapperStyle) {
    t15 = <div className={t12} style={wrapperStyle} data-slot-id={slotId} data-provider="custom">{t13}{t14}</div>;
    $[28] = slotId;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
    $[32] = wrapperStyle;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  return t15;
}

// ============================================================
// Ad Wrapper Component - Consistent styling for all ad slots
// ============================================================
function _temp(scriptInfo) {
  ;
  try {
    if (scriptInfo.src) {
      const newScript = document.createElement("script");
      newScript.src = scriptInfo.src;
      newScript.async = true;
      document.head.appendChild(newScript);
    } else {
      if (scriptInfo.content) {
        ;
        try {
          const fn = new Function(scriptInfo.content);
          fn();
        } catch (t1) {
          const evalErr = t1;
          try {
            const newScript_0 = document.createElement("script");
            newScript_0.textContent = scriptInfo.content;
            document.head.appendChild(newScript_0);
            newScript_0.remove();
          } catch {}
        }
      }
    }
  } catch (t0) {
    const err = t0;
  }
}
function AdWrapper(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <AdvertisePopup />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <div className="m-[5px]"><div className="bg-gray-100 p-[5px] relative">{children}{t1}</div></div>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

/**
 * Build CSS visibility classes based on component visibility settings
 * Uses Tailwind responsive classes for immediate CSS-based hiding (no JS hydration delay)
 *
 * Breakpoints:
 * - mobile: < 768px (default, no prefix)
 * - tablet: 768px - 1023px (md:)
 * - desktop: >= 1024px (lg:)
 */
function getVisibilityClasses(visibility?: ComponentVisibility): string {
  if (!visibility) return '';
  const hideMobile = visibility.mobile === false;
  const hideTablet = visibility.tablet === false;
  const hideDesktop = visibility.desktop === false;

  // If nothing is hidden, return empty
  if (!hideMobile && !hideTablet && !hideDesktop) return '';

  // If all hidden, hide completely
  if (hideMobile && hideTablet && hideDesktop) return 'hidden';
  const classes: string[] = [];

  // Handle mobile visibility (< 768px)
  if (hideMobile) {
    classes.push('hidden'); // Start hidden on mobile
  }

  // Handle tablet visibility (768px - 1023px)
  if (hideTablet) {
    if (!hideMobile) {
      classes.push('md:hidden'); // Hide starting from tablet
    }
    // If mobile is also hidden, keep hidden at md
  } else {
    if (hideMobile) {
      classes.push('md:block'); // Show starting from tablet
    }
  }

  // Handle desktop visibility (>= 1024px)
  if (hideDesktop) {
    classes.push('lg:hidden'); // Hide on desktop
  } else {
    if (hideMobile || hideTablet) {
      // If hidden on smaller screens but should show on desktop
      if (hideTablet) {
        classes.push('lg:block');
      }
    }
  }
  return classes.join(' ');
}
export default function WidgetRenderer(t0) {
  const $ = _c(130);
  const {
    component,
    includeSectionApiData
  } = t0;
  const adContainerRef = useRef(null);
  const [isMobile, deviceDetected] = useIsMobile();
  const [isUnderLg] = useIsMobile(1024);
  const isTablet = isUnderLg && !isMobile;
  const [adLoaded, setAdLoaded] = useState(false);
  const prevAdCodeRef = useRef(null);
  let t1;
  if ($[0] !== component.visibility) {
    t1 = getVisibilityClasses(component.visibility);
    $[0] = component.visibility;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const visibilityClasses = t1;
  let t2;
  bb0: {
    if (!component.visibility) {
      t2 = false;
      break bb0;
    }
    if (isMobile && component.visibility.mobile === false) {
      t2 = true;
      break bb0;
    }
    if (isTablet && component.visibility.tablet === false) {
      t2 = true;
      break bb0;
    }
    if (!isMobile && !isTablet && component.visibility.desktop === false) {
      t2 = true;
      break bb0;
    }
    t2 = false;
  }
  const isHiddenOnDevice = t2;
  let currentAdCode;
  let t3;
  if ($[2] !== component || $[3] !== isHiddenOnDevice || $[4] !== isMobile) {
    currentAdCode = isHiddenOnDevice ? undefined : getAdCode(component, isMobile);
    t3 = currentAdCode ? extractAdSize(currentAdCode) : null;
    $[2] = component;
    $[3] = isHiddenOnDevice;
    $[4] = isMobile;
    $[5] = currentAdCode;
    $[6] = t3;
  } else {
    currentAdCode = $[5];
    t3 = $[6];
  }
  const adSize = t3;
  let mobileAdSize;
  let t4;
  if ($[7] !== component) {
    const mobileAdCode = getAdCode(component, true);
    const desktopAdCode = getAdCode(component, false);
    mobileAdSize = mobileAdCode ? extractAdSize(mobileAdCode) : null;
    t4 = desktopAdCode ? extractAdSize(desktopAdCode) : null;
    $[7] = component;
    $[8] = mobileAdSize;
    $[9] = t4;
  } else {
    mobileAdSize = $[8];
    t4 = $[9];
  }
  const desktopAdSize = t4;
  let t5;
  let t6;
  if ($[10] !== adLoaded || $[11] !== component.provider || $[12] !== currentAdCode || $[13] !== deviceDetected || $[14] !== isHiddenOnDevice) {
    t5 = () => {
      if (!isLighthouseClient() && deviceDetected && currentAdCode && !isHiddenOnDevice && component.provider === "google" && adContainerRef.current) {
        if (prevAdCodeRef.current === currentAdCode && adLoaded) {
          return;
        }
        const container = adContainerRef.current;
        if (!container) {
          return;
        }
        const rect = container.getBoundingClientRect();
        const isNearViewport = rect.top < window.innerHeight + 300;
        const inject = () => {
          if (!adContainerRef.current) {
            return;
          }
          const sanitizedAdCode = sanitizeForDOM(currentAdCode);
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = sanitizedAdCode;
          const scripts = tempDiv.querySelectorAll("script");
          const nonScriptContent = sanitizedAdCode.replace(/<script[\s\S]*?<\/script>/gi, "");
          if (adContainerRef.current) {
            adContainerRef.current.innerHTML = nonScriptContent;
            scripts.forEach(_temp2);
            prevAdCodeRef.current = currentAdCode;
            setAdLoaded(true);
          }
        };
        if (isNearViewport) {
          const timer = setTimeout(inject, 0);
          return () => clearTimeout(timer);
        } else {
          const observer = new IntersectionObserver(entries => {
            if (entries[0]?.isIntersecting) {
              observer.disconnect();
              inject();
            }
          }, {
            rootMargin: "300px",
            threshold: 0
          });
          observer.observe(container);
          return () => observer.disconnect();
        }
      }
    };
    t6 = [currentAdCode, component.provider, adLoaded, isHiddenOnDevice, deviceDetected];
    $[10] = adLoaded;
    $[11] = component.provider;
    $[12] = currentAdCode;
    $[13] = deviceDetected;
    $[14] = isHiddenOnDevice;
    $[15] = t5;
    $[16] = t6;
  } else {
    t5 = $[15];
    t6 = $[16];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[17] !== currentAdCode) {
    t7 = () => {
      if (prevAdCodeRef.current !== null && prevAdCodeRef.current !== currentAdCode) {
        setAdLoaded(false);
      }
    };
    t8 = [currentAdCode];
    $[17] = currentAdCode;
    $[18] = t7;
    $[19] = t8;
  } else {
    t7 = $[18];
    t8 = $[19];
  }
  useEffect(t7, t8);
  if (component.slot_id && component.provider === "google") {
    const hasAnyAdCode = !!(component.desktopAdCode || component.mobileAdCode || component.instanceDesktopAdCode || component.instanceMobileAdCode || component.ad_code || currentAdCode);
    if (!hasAnyAdCode) {
      return null;
    }
    const placeholderSize = adSize || mobileAdSize || desktopAdSize;
    let t9;
    if ($[20] !== placeholderSize) {
      t9 = placeholderSize ? {
        maxWidth: "100%"
      } : {};
      $[20] = placeholderSize;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    const adContainerStyle = t9;
    const t10 = `ad-container flex justify-center items-center w-full ${visibilityClasses}`;
    let t11;
    if ($[22] !== t10) {
      t11 = t10.trim();
      $[22] = t10;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    let t12;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = {
        overflow: "hidden",
        maxWidth: "100%"
      };
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    const t13 = component.useInstanceAdCode ? "true" : "false";
    const t14 = isMobile ? "mobile" : "desktop";
    let t15;
    if ($[25] !== adContainerStyle || $[26] !== component.slot_id || $[27] !== t13 || $[28] !== t14) {
      t15 = <AdWrapper><div ref={adContainerRef} style={adContainerStyle} data-slot-id={component.slot_id} data-instance-ad={t13} data-device={t14} /></AdWrapper>;
      $[25] = adContainerStyle;
      $[26] = component.slot_id;
      $[27] = t13;
      $[28] = t14;
      $[29] = t15;
    } else {
      t15 = $[29];
    }
    let t16;
    if ($[30] !== t11 || $[31] !== t15) {
      t16 = <div className={t11} style={t12}>{t15}</div>;
      $[30] = t11;
      $[31] = t15;
      $[32] = t16;
    } else {
      t16 = $[32];
    }
    return t16;
  }
  if (component.slot_id && component.provider === "custom") {
    const htmlContent = component.customTemplateHtml;
    const cssContent = component.customTemplateCss;
    const jsContent = component.customTemplateJs;
    const widthValue = typeof component.width === "string" ? component.width : component.width?.desktop;
    const heightValue = typeof component.height === "string" ? component.height : component.height?.desktop;
    if (htmlContent) {
      const t9 = `ad-container flex justify-center items-center w-full ${visibilityClasses}`;
      let t10;
      if ($[33] !== t9) {
        t10 = t9.trim();
        $[33] = t9;
        $[34] = t10;
      } else {
        t10 = $[34];
      }
      let t11;
      if ($[35] !== component.id || $[36] !== component.slot_id || $[37] !== cssContent || $[38] !== heightValue || $[39] !== htmlContent || $[40] !== jsContent || $[41] !== widthValue) {
        t11 = <AdWrapper><CustomAdWidget id={component.id} slotId={component.slot_id} htmlContent={htmlContent} cssContent={cssContent} jsContent={jsContent} width={widthValue} height={heightValue} visibilityClasses="" /></AdWrapper>;
        $[35] = component.id;
        $[36] = component.slot_id;
        $[37] = cssContent;
        $[38] = heightValue;
        $[39] = htmlContent;
        $[40] = jsContent;
        $[41] = widthValue;
        $[42] = t11;
      } else {
        t11 = $[42];
      }
      let t12;
      if ($[43] !== t10 || $[44] !== t11) {
        t12 = <div className={t10}>{t11}</div>;
        $[43] = t10;
        $[44] = t11;
        $[45] = t12;
      } else {
        t12 = $[45];
      }
      return t12;
    }
    return null;
  }
  if (component.widget_type === "iframe" && component.url) {
    const iframeWidth = typeof component.width === "string" ? component.width : component.width?.desktop || "100%";
    const iframeHeight = typeof component.height === "string" ? component.height : component.height?.desktop || "280px";
    const t9 = `iframe-container w-full ${visibilityClasses}`;
    let t10;
    if ($[46] !== t9) {
      t10 = t9.trim();
      $[46] = t9;
      $[47] = t10;
    } else {
      t10 = $[47];
    }
    let t11;
    if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = {
        overflow: "hidden",
        maxWidth: "100%"
      };
      $[48] = t11;
    } else {
      t11 = $[48];
    }
    const t12 = component.title || "Embedded Content";
    const t13 = component.loading as 'lazy' | 'eager' || "lazy";
    let t14;
    if ($[49] !== iframeHeight || $[50] !== iframeWidth) {
      t14 = {
        width: iframeWidth,
        height: iframeHeight,
        maxWidth: "100%"
      };
      $[49] = iframeHeight;
      $[50] = iframeWidth;
      $[51] = t14;
    } else {
      t14 = $[51];
    }
    let t15;
    if ($[52] !== component.allowFullscreen || $[53] !== component.url || $[54] !== iframeHeight || $[55] !== iframeWidth || $[56] !== t12 || $[57] !== t13 || $[58] !== t14) {
      t15 = <iframe src={component.url} title={t12} width={iframeWidth} height={iframeHeight} loading={t13} allowFullScreen={component.allowFullscreen} className="w-full" style={t14} />;
      $[52] = component.allowFullscreen;
      $[53] = component.url;
      $[54] = iframeHeight;
      $[55] = iframeWidth;
      $[56] = t12;
      $[57] = t13;
      $[58] = t14;
      $[59] = t15;
    } else {
      t15 = $[59];
    }
    let t16;
    if ($[60] !== t10 || $[61] !== t15) {
      t16 = <div className={t10} style={t11}>{t15}</div>;
      $[60] = t10;
      $[61] = t15;
      $[62] = t16;
    } else {
      t16 = $[62];
    }
    return t16;
  }
  if (component.widget_type === "image" && component.url) {
    const imageHeight = typeof component.height === "string" ? component.height : component.height?.desktop || "auto";
    let t9;
    if ($[63] !== component.link || $[64] !== component.linkTarget) {
      t9 = getWidgetLinkProps(component.link, component.linkTarget);
      $[63] = component.link;
      $[64] = component.linkTarget;
      $[65] = t9;
    } else {
      t9 = $[65];
    }
    const linkProps = t9;
    let t10;
    if ($[66] !== imageHeight) {
      t10 = {
        height: imageHeight,
        overflow: "hidden",
        maxWidth: "100%"
      };
      $[66] = imageHeight;
      $[67] = t10;
    } else {
      t10 = $[67];
    }
    const t11 = component.alt || component.title || "Image";
    const t12 = imageHeight !== "auto";
    const t13 = imageHeight === "auto" ? 1200 : undefined;
    const t14 = imageHeight === "auto" ? 600 : undefined;
    const t15 = `${component.objectFit === "cover" ? "object-cover" : "object-contain"} w-full`;
    const t16 = component.loading as 'lazy' | 'eager' || "eager";
    const t17 = !component.loading || component.loading === "eager";
    let t18;
    if ($[68] !== component.url || $[69] !== t11 || $[70] !== t12 || $[71] !== t13 || $[72] !== t14 || $[73] !== t15 || $[74] !== t16 || $[75] !== t17) {
      t18 = <Image src={component.url} alt={t11} fill={t12} width={t13} height={t14} className={t15} loading={t16} priority={t17} placeholder="blur" blurDataURL={BLUR_PLACEHOLDER} />;
      $[68] = component.url;
      $[69] = t11;
      $[70] = t12;
      $[71] = t13;
      $[72] = t14;
      $[73] = t15;
      $[74] = t16;
      $[75] = t17;
      $[76] = t18;
    } else {
      t18 = $[76];
    }
    let t19;
    if ($[77] !== t10 || $[78] !== t18) {
      t19 = <div className="relative w-full" style={t10}>{t18}</div>;
      $[77] = t10;
      $[78] = t18;
      $[79] = t19;
    } else {
      t19 = $[79];
    }
    const imageElement = t19;
    const t20 = `image-widget w-full ${visibilityClasses}`;
    let t21;
    if ($[80] !== t20) {
      t21 = t20.trim();
      $[80] = t20;
      $[81] = t21;
    } else {
      t21 = $[81];
    }
    let t22;
    if ($[82] !== imageElement || $[83] !== linkProps) {
      t22 = linkProps ? linkProps.useNextLink ? <Link href={linkProps.href} target={linkProps.target} rel={linkProps.rel}>{imageElement}</Link> : <a href={linkProps.href} target={linkProps.target} rel={linkProps.rel}>{imageElement}</a> : imageElement;
      $[82] = imageElement;
      $[83] = linkProps;
      $[84] = t22;
    } else {
      t22 = $[84];
    }
    let t23;
    if ($[85] !== t21 || $[86] !== t22) {
      t23 = <div className={t21}>{t22}</div>;
      $[85] = t21;
      $[86] = t22;
      $[87] = t23;
    } else {
      t23 = $[87];
    }
    return t23;
  }
  if (component.widget_type === "html" && component.htmlContent) {
    let t9;
    if ($[88] !== component.cssContent || $[89] !== component.htmlContent || $[90] !== component.id || $[91] !== component.jsContent || $[92] !== visibilityClasses) {
      t9 = <HTMLWidget id={component.id} htmlContent={component.htmlContent} cssContent={component.cssContent} jsContent={component.jsContent} visibilityClasses={visibilityClasses} />;
      $[88] = component.cssContent;
      $[89] = component.htmlContent;
      $[90] = component.id;
      $[91] = component.jsContent;
      $[92] = visibilityClasses;
      $[93] = t9;
    } else {
      t9 = $[93];
    }
    return t9;
  }
  if (component.widget_type === "include-section" && component.slug) {
    let t9;
    if ($[94] !== component.slug) {
      t9 = getSectionComponent(component.slug);
      $[94] = component.slug;
      $[95] = t9;
    } else {
      t9 = $[95];
    }
    const SectionComponent = t9;
    if (SectionComponent) {
      const sectionSlug = component.slug;
      let t10;
      if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = {};
        $[96] = t10;
      } else {
        t10 = $[96];
      }
      let sectionProps = t10;
      if (sectionSlug === "trending-tab" || sectionSlug === "recent-news") {
        let t11;
        if ($[97] !== includeSectionApiData?.trendingStories) {
          t11 = includeSectionApiData?.trendingStories || [];
          $[97] = includeSectionApiData?.trendingStories;
          $[98] = t11;
        } else {
          t11 = $[98];
        }
        let t12;
        if ($[99] !== includeSectionApiData?.commentedStories) {
          t12 = includeSectionApiData?.commentedStories || [];
          $[99] = includeSectionApiData?.commentedStories;
          $[100] = t12;
        } else {
          t12 = $[100];
        }
        let t13;
        if ($[101] !== t11 || $[102] !== t12) {
          t13 = {
            trendingStories: t11,
            commentedStories: t12
          };
          $[101] = t11;
          $[102] = t12;
          $[103] = t13;
        } else {
          t13 = $[103];
        }
        sectionProps = t13;
      } else {
        if (sectionSlug === "image-video" || sectionSlug === "page-foot-section") {
          let t11;
          if ($[104] !== includeSectionApiData?.galleries) {
            t11 = includeSectionApiData?.galleries || [];
            $[104] = includeSectionApiData?.galleries;
            $[105] = t11;
          } else {
            t11 = $[105];
          }
          let t12;
          if ($[106] !== includeSectionApiData?.videos) {
            t12 = includeSectionApiData?.videos || [];
            $[106] = includeSectionApiData?.videos;
            $[107] = t12;
          } else {
            t12 = $[107];
          }
          let t13;
          if ($[108] !== t11 || $[109] !== t12) {
            t13 = {
              galleries: t11,
              videos: t12
            };
            $[108] = t11;
            $[109] = t12;
            $[110] = t13;
          } else {
            t13 = $[110];
          }
          sectionProps = t13;
        } else {
          if (sectionSlug === "breakingnews") {
            let t11;
            if ($[111] === Symbol.for("react.memo_cache_sentinel")) {
              t11 = {};
              $[111] = t11;
            } else {
              t11 = $[111];
            }
            sectionProps = t11;
          } else {
            if (sectionSlug === "trendingtags") {
              let t11;
              if ($[112] !== includeSectionApiData?.trendingTags) {
                t11 = includeSectionApiData?.trendingTags || [];
                $[112] = includeSectionApiData?.trendingTags;
                $[113] = t11;
              } else {
                t11 = $[113];
              }
              let t12;
              if ($[114] !== t11) {
                t12 = {
                  tags: t11
                };
                $[114] = t11;
                $[115] = t12;
              } else {
                t12 = $[115];
              }
              sectionProps = t12;
            }
          }
        }
      }
      const t11 = `include-section-widget ${visibilityClasses}`;
      let t12;
      if ($[116] !== t11) {
        t12 = t11.trim();
        $[116] = t11;
        $[117] = t12;
      } else {
        t12 = $[117];
      }
      let t13;
      if ($[118] !== SectionComponent || $[119] !== sectionProps) {
        t13 = <SectionComponent {...sectionProps} />;
        $[118] = SectionComponent;
        $[119] = sectionProps;
        $[120] = t13;
      } else {
        t13 = $[120];
      }
      let t14;
      if ($[121] !== component.slug || $[122] !== t12 || $[123] !== t13) {
        t14 = <div className={t12} data-section-slug={component.slug}>{t13}</div>;
        $[121] = component.slug;
        $[122] = t12;
        $[123] = t13;
        $[124] = t14;
      } else {
        t14 = $[124];
      }
      return t14;
    }
    const t10 = `include-section-placeholder p-4 text-center text-gray-400 ${visibilityClasses}`;
    let t11;
    if ($[125] !== t10) {
      t11 = t10.trim();
      $[125] = t10;
      $[126] = t11;
    } else {
      t11 = $[126];
    }
    const t12 = component.name || component.slug;
    let t13;
    if ($[127] !== t11 || $[128] !== t12) {
      t13 = <div className={t11}>Section: {t12}</div>;
      $[127] = t11;
      $[128] = t12;
      $[129] = t13;
    } else {
      t13 = $[129];
    }
    return t13;
  }
  return null;
}
function _temp2(script) {
  try {
    const newScript = document.createElement("script");
    Array.from(script.attributes).forEach(attr => {
      try {
        const attrName = attr.name.replace(/[^a-zA-Z0-9-_]/g, "");
        const attrValue = sanitizeForDOM(attr.value);
        if (attrName) {
          newScript.setAttribute(attrName, attrValue);
        }
      } catch {}
    });
    if (script.textContent) {
      const sanitizedScript = sanitizeForDOM(script.textContent);
      try {
        const fn = new Function(sanitizedScript);
        fn();
      } catch {
        try {
          newScript.textContent = sanitizedScript;
          document.head.appendChild(newScript);
        } catch {}
      }
    } else {
      if (script.src) {
        document.head.appendChild(newScript);
      }
    }
  } catch {}
}